import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeroSection } from './components/HeroSection';
import { LocalExpertSection } from './components/LocalExpertSection';
import { BestToursSection } from './components/BestToursSection';
import { ServicesSection } from './components/ServicesSection';
import { EstimateSection } from './components/EstimateSection';
import { PosterSection } from './components/PosterSection';
import { VehiclesSection } from './components/VehiclesSection';
import { PortfolioSection } from './components/PortfolioSection';
import { TestimonialsSection } from './components/TestimonialsSection';
import { TravelInfoSection } from './components/TravelInfoSection';
import { BookingSection } from './components/BookingSection';
import { ContactSection } from './components/ContactSection';
import { Footer } from './components/Footer';
import { KakaoContactModal } from './components/KakaoContactModal';
import { SEOJsonLd } from '@/components/feature/SEOJsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.jejuvely.com';

const travelAgencySchema = {
  '@context': 'https://schema.org',
  '@type': 'TravelAgency',
  name: '제주블리',
  alternateName: 'Jejuvely',
  description: '제주 로컬 전문가가 큐레이션하는 맞춤형 프리미엄 여행 서비스',
  url: siteUrl,
  telephone: '+82-10-5685-7818',
  faxNumber: '+82-50-4258-7818',
  email: 'jejuvely@gmail.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '연미길 32',
    addressLocality: '제주시',
    addressRegion: '제주특별자치도',
    postalCode: '63048',
    addressCountry: 'KR',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '33.4996',
    longitude: '126.5312',
  },
  priceRange: '₩₩₩',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '500',
    bestRating: '5',
  },
  slogan: '제주를 가장 제주답게',
  founder: {
    '@type': 'Person',
    name: '권병주',
  },
  areaServed: {
    '@type': 'State',
    name: '제주특별자치도',
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '08:00',
      closes: '21:00',
    },
  ],
  sameAs: [
    'https://instagram.com/jejuvelycom',
    'https://www.youtube.com/@%EC%A0%9C%EC%A3%BC%EB%B8%94%EB%A6%AC',
  ],
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: '제주 맞춤형 로컬 여행 서비스',
  provider: {
    '@type': 'TravelAgency',
    name: '제주블리 Jejuvely',
    url: siteUrl,
  },
  areaServed: {
    '@type': 'City',
    name: '제주특별자치도',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: '제주 프리미엄 여행 서비스',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '택시 투어 / 벤츠 스프린터 / 대형 VIP 리무진',
          description: '전문 드라이버가 함께하는 제주 프리미엄 차량 투어',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '웨딩 스냅 / CF & 방송 촬영 / 워크숍 이동',
          description: '제주의 아름다운 배경으로 특별한 순간을 담는 촬영 서비스',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '골프 부킹 / 한라산 등반 / 힐링 자유 여행',
          description: '골프, 트레킹, 자유 여행 맞춤 패키지',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '동부 코스 투어',
          description: '성산일출봉, 섭지코지, 우도, 만장굴',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '서부 코스 투어',
          description: '협재해수욕장, 한림공원, 오설록, 카멜리아힐',
        },
      },
    ],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '제주블리는 어떤 여행사인가요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '제주블리는 제주 로컬 전문가가 직접 큐레이션하는 맞춤형 프리미엄 여행 서비스를 제공합니다. 숨은 명소 소개, 1:1 맞춤 여행 설계, 진정성 있는 스토리텔링으로 제주를 가장 제주답게 경험할 수 있도록 도와드립니다.',
      },
    },
    {
      '@type': 'Question',
      name: '어떤 투어 코스가 있나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '동부 코스(성산일출봉, 섭지코지, 우도), 서부 코스(협재해수욕장, 한림공원, 오설록), 한라산 등반 코스, 맛집 투어, 골프 부킹, 웨딩 스냅, 택시 투어, 벤츠 스프린터, 대형 VIP 리무진 등 다양한 프리미엄 제주 여행 서비스를 제공합니다.',
      },
    },
    {
      '@type': 'Question',
      name: '예약은 어떻게 하나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '전화(010-5685-7818 또는 064-782-7818), 카카오톡, 또는 웹사이트 실시간 상담을 통해 예약하실 수 있습니다. 로컬 전문가가 직접 상담하여 고객님께 최적화된 여행 플랜을 제안해드립니다.',
      },
    },
    {
      '@type': 'Question',
      name: '제주블리 연락처와 주소는 어디인가요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '주소: 제주시 연미길 32 (오라삼동), 전화: 064-782-7818, 핸드폰: 010-5685-7818, 이메일: jejuvely@gmail.com',
      },
    },
    {
      '@type': 'Question',
      name: '제주블리의 대표 차량은 무엇인가요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '벤츠 스프린터 9인승, 14인승 대형 리무진 등 프리미엄 차량으로 제주 여행을 제공합니다. 전문 드라이버가 안전하고 편안한 여행을 책임집니다.',
      },
    },
    {
      '@type': 'Question',
      name: '제주 여행 추천 시즌은 언제인가요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '제주는 사시사철 아름답지만, 봄(3-5월)에는 유채꽃과 벚꽃, 여름(6-8월)에는 핫플 핫한 바다, 가을(9-11월)에는 단풍과 억새, 겨울(12-2월)에는 한라산 눈꽃이 특별합니다. 365일 언제든 제주블리와 함께하세요.',
      },
    },
  ],
};

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '제주블리 Jejuvely',
  alternateName: '제주 로컬 전문가 여행사',
  url: siteUrl,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteUrl}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
  inLanguage: 'ko-KR',
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '제주 로컬 전문가 1위 제주블리 Jejuvely - 제주를 가장 제주답게',
  description:
    '제주를 가장 제주답게 경험하는 방법. 로컬 전문가가 큐레이션하는 숨은 명소와 1:1 맞춤 여행 설계.',
  url: siteUrl,
  inLanguage: 'ko-KR',
  isPartOf: {
    '@type': 'WebSite',
    name: '제주블리 Jejuvely',
    url: siteUrl,
  },
  about: {
    '@type': 'TravelAgency',
    name: '제주블리',
  },
  dateModified: '2026-04-01',
  datePublished: '2024-01-01',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '홈',
        item: siteUrl,
      },
    ],
  },
};

export default function HomePage() {
  const [showKakaoTooltip, setShowKakaoTooltip] = useState(false);
  const [showKakaoModal, setShowKakaoModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = () => setShowKakaoModal(true);
    window.addEventListener('openKakaoModal', handler);
    return () => window.removeEventListener('openKakaoModal', handler);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOJsonLd data={[travelAgencySchema, serviceSchema, faqSchema, webSiteSchema, webPageSchema]} />
      <KakaoContactModal isOpen={showKakaoModal} onClose={() => setShowKakaoModal(false)} />
      <HeroSection />
      <LocalExpertSection />
      <BestToursSection />
      <ServicesSection />
      <EstimateSection />
      <PosterSection />
      <VehiclesSection />
      <PortfolioSection />
      <TestimonialsSection />
      <TravelInfoSection />
      <BookingSection />
      <ContactSection />
      <Footer />

      {/* 플로팅 카카오톡 버튼 */}
      <AnimatePresence>
        {scrolled && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-8 right-8 z-50 flex flex-col items-end gap-3"
          >
            {/* 툴팁 */}
            <AnimatePresence>
              {showKakaoTooltip && (
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="bg-[#2C2C2C] text-white text-sm font-medium px-4 py-2 rounded-xl whitespace-nowrap"
                >
                  카카오톡으로 바로 상담하기
                  <div className="absolute right-3 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-[#2C2C2C]"></div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* 카카오 버튼 */}
            <button
              onClick={() => setShowKakaoModal(true)}
              onMouseEnter={() => setShowKakaoTooltip(true)}
              onMouseLeave={() => setShowKakaoTooltip(false)}
              className="w-16 h-16 bg-[#FEE500] rounded-full flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95 transition-transform"
              style={{ boxShadow: '0 4px 20px rgba(254,229,0,0.5)' }}
              aria-label="카카오톡 상담"
            >
              <i className="ri-kakao-talk-fill text-3xl text-[#3C1E1E]"></i>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
